import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, Wifi, WifiOff, Settings, RotateCcw, Edit2 } from 'lucide-react'
import useSettingsStore from '../../stores/settingsStore'
import useSurveyStore from '../../stores/surveyStore'
import { db } from '../../db/database'

export default function Header({ onMenuClick, onSurveyDetailsClick, showMenuButton = true }) {
  const location = useLocation()
  const isOnline = useSettingsStore(state => state.isOnline)
  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const [resetting, setResetting] = useState(false)

  // Check if we're on a survey-related page
  const isSurveyPage = location.pathname.startsWith('/survey/') && !location.pathname.includes('/new')

  // Reset database handler
  const handleResetDatabase = async () => {
    if (!window.confirm('Reset database? This will delete ALL surveys, photos and settings. The app will reload with fresh data.')) {
      return
    }

    setResetting(true)
    try {
      await db.delete()
      window.location.reload()
    } catch (error) {
      console.error('Failed to reset database:', error)
      alert('Failed to reset database. Please try clearing browser data manually.')
      setResetting(false)
    }
  }

  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname
    if (path === '/') return 'Dashboard'
    if (path === '/surveys') return 'All Surveys'
    if (path === '/survey/new') return 'New Survey'
    if (path.startsWith('/survey/') && path.includes('/analysis')) return 'Analysis Results'
    if (path.startsWith('/survey/')) return 'Survey'
    if (path === '/admin') return 'Manage Categories'
    if (path === '/settings') return 'Settings'
    return 'Clystcore Surveyors'
  }

  return (
    <header className="bg-primary-700 text-white shadow-lg sticky top-0 z-40">
      <div className="flex items-center justify-between px-4 py-4">
        {/* Left side - Menu, Logo and Survey Info */}
        <div className="flex items-center gap-3">
          {showMenuButton && (
            <button
              onClick={onMenuClick}
              className="p-2 hover:bg-primary-600 rounded-lg transition-colors lg:hidden"
              aria-label="Toggle menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          )}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://cdn.prod.website-files.com/692966ae0a71b2cba119f627/69297d940233db07bdb7b960_nav-logo.avif"
              alt="Clystcore Surveyors"
              className="h-10 w-auto"
            />
          </Link>

          {/* Survey Address Panel - shown when on survey pages */}
          {isSurveyPage && currentSurvey && (
            <button
              onClick={onSurveyDetailsClick}
              className="hidden sm:flex items-center gap-2 ml-4 px-3 py-1.5 bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors group"
              title="Click to edit survey details"
            >
              <div className="text-left">
                <div className="text-sm font-medium text-white truncate max-w-[200px]">
                  {currentSurvey.propertyName}
                </div>
                <div className="text-xs text-primary-200 truncate max-w-[200px]">
                  {currentSurvey.address}, {currentSurvey.postcode}
                </div>
              </div>
              <Edit2 className="w-4 h-4 text-primary-300 group-hover:text-white transition-colors" />
            </button>
          )}
        </div>

        {/* Center - Page Title */}
        <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block">
          <h2 className="text-lg font-medium">{getPageTitle()}</h2>
        </div>

        {/* Right side - Status and Settings */}
        <div className="flex items-center gap-2">
          {/* Online/Offline indicator */}
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
              isOnline ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'
            }`}
          >
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4" />
                <span className="hidden sm:inline">Online</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4" />
                <span className="hidden sm:inline">Offline</span>
              </>
            )}
          </div>

          {/* Reset database button */}
          <button
            onClick={handleResetDatabase}
            disabled={resetting}
            className="p-2 hover:bg-red-500/30 rounded-lg transition-colors"
            aria-label="Reset database"
            title="Reset database"
          >
            <RotateCcw className={`w-5 h-5 ${resetting ? 'animate-spin' : ''}`} />
          </button>

          {/* Settings link */}
          <Link
            to="/settings"
            className="p-2 hover:bg-primary-600 rounded-lg transition-colors"
            aria-label="Settings"
          >
            <Settings className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Mobile page title */}
      <div className="md:hidden px-4 pb-3">
        <h2 className="text-sm font-medium text-primary-200">{getPageTitle()}</h2>
      </div>
    </header>
  )
}
